import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Calendar, Printer, Users, Lightbulb } from 'lucide-react';

// eslint-disable-next-line max-lines-per-function
export const RoutePreviewGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Route Preview'
      description='Review and confirm technician routes before execution'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Route Preview' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Hero Section */}
        <section className='mb-20'>
          <div className='not-prose'>
            <Card className='border-primary/50 bg-primary/5 rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Eye className='h-8 w-8 text-primary' />
                  </div>
                  <div>
                    <CardTitle className='text-lg font-bold'>What is Route Preview?</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Route Preview is the final checkpoint before technicians head out for the day.
                  Review all assigned routes, confirm assignments are correct, and print route
                  sheets for technicians. This page shows only draft and in-progress routes, helping
                  you focus on work that still needs attention.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Who Can Access Route Preview</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            Route Preview is available to users with these roles:
          </p>
          <ul className='space-y-2 text-sm leading-6'>
            <li>Service Advisors</li>
            <li>Service Managers</li>
            <li>Field Operations Directors</li>
            <li>Lead Technicians</li>
            <li>Dealership Admins</li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Accessing Route Preview</h2>
          <p className='text-sm text-muted-foreground mb-4 leading-6'>
            Navigate to Route Preview from the sidebar:
          </p>
          <p className='text-sm leading-6'>
            <strong>Sidebar → PLAN → Route Preview</strong>
          </p>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Key Features</h2>

          <div className='not-prose space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Calendar className='h-5 w-5 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Date Selection</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Use the date picker at the top to view routes for any date. The page defaults to
                  today&apos;s date in your dealership&apos;s timezone. Select a future date to
                  preview upcoming routes, or a past date to review historical assignments.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Users className='h-5 w-5 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Technician Sections</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Routes are organized by technician. Each technician&apos;s section is collapsible
                  - click to expand and see their assigned dealers and recalls. This makes it easy
                  to review workload distribution at a glance.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Printer className='h-5 w-5 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Print Functionality</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 mb-4'>
                  Print route sheets for technicians to take on the road. You have three print
                  options:
                </p>
                <ul className='space-y-2 text-sm leading-6'>
                  <li>
                    <strong>Print Individual Dealer:</strong> Print recalls for a single dealer stop
                  </li>
                  <li>
                    <strong>Print All Dealers (per technician):</strong> Print all stops for one
                    technician
                  </li>
                  <li>
                    <strong>Print All Routes:</strong> Print every technician&apos;s complete route
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Understanding Route Status</h2>

          <div className='not-prose'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='pt-6'>
                <p className='text-sm leading-6 mb-4'>
                  Routes have different statuses that indicate their progress:
                </p>
                <div className='space-y-3'>
                  <div className='flex items-center gap-3'>
                    <Badge className='bg-gray-100 text-gray-800'>Draft</Badge>
                    <span className='text-sm'>
                      Route is planned but not yet confirmed. Can still be modified.
                    </span>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Badge className='bg-blue-100 text-blue-800'>Confirmed</Badge>
                    <span className='text-sm'>
                      Route is locked and ready for execution. Does not appear on preview page.
                    </span>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Badge className='bg-yellow-100 text-yellow-800'>In Progress</Badge>
                    <span className='text-sm'>Technician has started working on this route.</span>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Badge className='bg-green-100 text-green-800'>Completed</Badge>
                    <span className='text-sm'>
                      All recalls in the route have been addressed. Does not appear on preview page.
                    </span>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Badge className='bg-red-100 text-red-800'>Cancelled</Badge>
                    <span className='text-sm'>Route was cancelled and will not be executed.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Daily Workflow</h2>

          <div className='not-prose space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-2'>
                  <Badge>Step 1</Badge>
                  <CardTitle className='text-lg font-semibold'>Review Routes</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Open Route Preview and select today&apos;s date. Expand each technician&apos;s
                  section to review their assigned dealers and recalls. Verify the workload is
                  balanced and appropriate for each technician&apos;s skill level.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-2'>
                  <Badge>Step 2</Badge>
                  <CardTitle className='text-lg font-semibold'>Make Adjustments</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  If you need to make changes, return to{' '}
                  <Link to='/docs/features/manual-routing' className='text-primary hover:underline'>
                    Manual Routing
                  </Link>{' '}
                  to reassign recalls. Changes are reflected immediately in Route Preview.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-2'>
                  <Badge>Step 3</Badge>
                  <CardTitle className='text-lg font-semibold'>Print Route Sheets</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Once routes are finalized, print route sheets for each technician. The printed
                  sheets include dealer addresses, recall details, and stop sequence numbers.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-2'>
                  <Badge>Step 4</Badge>
                  <CardTitle className='text-lg font-semibold'>Track Progress</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  As technicians complete their work, track progress in the{' '}
                  <Link to='/docs/features/route-tracker' className='text-primary hover:underline'>
                    Route Tracker
                  </Link>
                  . Update recall statuses and monitor completion rates.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Tips for Effective Route Management</h2>

          <div className='not-prose'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='pt-6'>
                <div className='space-y-4'>
                  <div className='flex items-start gap-3'>
                    <Lightbulb className='h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5' />
                    <p className='text-sm leading-6'>
                      <strong>Review early:</strong> Check Route Preview first thing in the morning
                      to catch any issues before technicians leave.
                    </p>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Lightbulb className='h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5' />
                    <p className='text-sm leading-6'>
                      <strong>Balance workloads:</strong> Ensure no technician is overloaded. Use
                      the stop count and recall count to gauge workload.
                    </p>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Lightbulb className='h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5' />
                    <p className='text-sm leading-6'>
                      <strong>Consider skills:</strong> Verify that recalls requiring higher skill
                      levels are assigned to appropriately skilled technicians.
                    </p>
                  </div>
                  <div className='flex items-start gap-3'>
                    <Lightbulb className='h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5' />
                    <p className='text-sm leading-6'>
                      <strong>Plan ahead:</strong> Use the date picker to review tomorrow&apos;s
                      routes before leaving for the day.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Common Questions</h2>

          <div className='not-prose space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base font-semibold'>
                  Why don&apos;t I see all routes on this page?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Route Preview only shows draft and in-progress routes. Completed and confirmed
                  routes are filtered out to help you focus on work that needs attention. To see all
                  routes including completed ones, visit the{' '}
                  <Link to='/docs/features/route-tracker' className='text-primary hover:underline'>
                    Route Tracker
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base font-semibold'>
                  Can I edit routes from this page?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Route Preview is for reviewing and printing only. To make changes to route
                  assignments, use the{' '}
                  <Link to='/docs/features/manual-routing' className='text-primary hover:underline'>
                    Manual Routing
                  </Link>{' '}
                  page.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-2'>
                <CardTitle className='text-base font-semibold'>
                  What timezone does the date picker use?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  The date picker uses your dealership&apos;s configured timezone. This ensures that
                  routes are shown for the correct business day regardless of your current location.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Related Features</h2>

          {/* eslint-disable max-len */}
          <div className='not-prose grid gap-4 md:grid-cols-2'>
            <Link to='/docs/features/manual-routing'>
              <Card className='h-full hover:border-primary transition-colors cursor-pointer rounded-xl border shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-base font-semibold'>Manual Routing</CardTitle>
                  <CardDescription className='text-sm'>
                    Assign recalls to technicians and create routes
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to='/docs/features/route-tracker'>
              <Card className='h-full hover:border-primary transition-colors cursor-pointer rounded-xl border shadow-sm'>
                <CardHeader>
                  <CardTitle className='text-base font-semibold'>Route Tracker</CardTitle>
                  <CardDescription className='text-sm'>
                    Track technician progress and update recall statuses
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>
          {/* eslint-enable max-len */}
        </section>
      </div>
    </DocsLayout>
  );
};
